"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Send } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactoRapido() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <section ref={ref} className="bg-light-background py-20 px-6 md:px-12 lg:px-20">
      <div className={`max-w-7xl mx-auto transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
        <div className="grid lg:grid-cols-[1fr_40%] gap-12 items-center">
          {}
          <div className="space-y-6">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-foreground-light leading-tight">
              Hablemos de tu proyecto
            </h2>
            <p className="text-lg text-foreground-light/80 leading-relaxed max-w-xl">
              Estamos listos para ayudarte a optimizar tu gestión comercial y presencia digital. 
              Completa el formulario y nuestro equipo te contactará en menos de 24 horas.
            </p>
            <div className="space-y-4 pt-4">
              <div className="flex items-start gap-3">
                <div className="size-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                  <div className="size-2 rounded-full bg-primary"></div>
                </div>
                <p className="text-foreground-light/70">
                  <span className="font-semibold text-foreground-light">Respuesta rápida:</span> Te contactamos en menos de 24 horas
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="size-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                  <div className="size-2 rounded-full bg-primary"></div>
                </div>
                <p className="text-foreground-light/70">
                  <span className="font-semibold text-foreground-light">Consultoría inicial sin compromiso</span>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="size-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                  <div className="size-2 rounded-full bg-primary"></div>
                </div>
                <p className="text-foreground-light/70">
                  <span className="font-semibold text-foreground-light">Soluciones personalizadas</span> para tu sector
                </p>
              </div>
            </div>
          </div>

          {}
          <FormProvider className="bg-background rounded-lg shadow-medium p-8 border border-border" formId="contact-form-quick" sectionName="contacto-rapido" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false,
            minLength: 9,
            maxLength: 20
          },
          message: {
            required: true,
            minLength: 10,
            maxLength: 1000
          }
        }} submitText="Enviar mensaje" successMessage="¡Gracias! Nos pondremos en contacto contigo pronto.">
            <ContactFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center space-y-6 py-8">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Send className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-display text-2xl font-bold text-foreground-light">
            ¡Mensaje enviado!
          </h3>
          <p className="text-foreground-light/70">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="transition-all duration-[120ms] ease-in-out hover:-translate-y-0.5">
          Enviar otro mensaje
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio",
        minLength: {
          value: 2,
          message: "Mínimo 2 caracteres"
        },
        maxLength: {
          value: 100,
          message: "Máximo 100 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Nombre completo</FormLabel>
              <FormControl>
                <Input placeholder="Tu nombre" className="transition-all duration-[120ms] focus:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El email es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Email inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="tu@email.com" className="transition-all duration-[120ms] focus:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        minLength: {
          value: 9,
          message: "Mínimo 9 dígitos"
        },
        maxLength: {
          value: 20,
          message: "Máximo 20 dígitos"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Teléfono (opcional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+34 912 345 678" className="transition-all duration-[120ms] focus:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "El mensaje es obligatorio",
        minLength: {
          value: 10,
          message: "Mínimo 10 caracteres"
        },
        maxLength: {
          value: 1000,
          message: "Máximo 1000 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Mensaje</FormLabel>
              <FormControl>
                <Textarea placeholder="Cuéntanos sobre tu proyecto..." className="min-h-28 transition-all duration-[120ms] focus:border-primary resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full transition-all duration-[120ms] ease-in-out hover:-translate-y-0.5 hover:shadow-medium">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Enviando...
            </> : <>
              <Send className="mr-2 size-4" />
              Enviar mensaje
            </>}
        </Button>
      </div>
    </Form>;
}